import {
  COMMON_CURRENCY_CODES,
  getPreferredCurrencyForCountry,
  getStoredUserCountryCode,
  LOCATION_INITIALIZED_EVENT,
} from '@/lib/location';
import { useEffect, useState } from 'react';

const DEFAULT_CURRENCY = 'USD';
const USER_CURRENCY_STORAGE_KEY = 'user-currency';

const getBrowserLocaleCountryCode = () => {
  if (typeof navigator === 'undefined') return null;

  for (const locale of navigator.languages?.length ? navigator.languages : [navigator.language]) {
    try {
      const region = new Intl.Locale(locale).region;
      if (region) return region;
    } catch {
      // Ignore malformed browser locale values and try the next one.
    }
  }

  return null;
};

const getStoredCurrency = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_CURRENCY;
  }

  const storedCurrency = localStorage.getItem(USER_CURRENCY_STORAGE_KEY)?.toUpperCase();

  if (!storedCurrency) {
    const storedCountry = getStoredUserCountryCode() || getBrowserLocaleCountryCode();
    if (!storedCountry) {
      return DEFAULT_CURRENCY;
    }

    const countryCurrency = getPreferredCurrencyForCountry(storedCountry, COMMON_CURRENCY_CODES);
    localStorage.setItem(USER_CURRENCY_STORAGE_KEY, countryCurrency);
    return countryCurrency;
  }

  if (!(COMMON_CURRENCY_CODES as readonly string[]).includes(storedCurrency)) {
    const storedCountry = getStoredUserCountryCode();
    const fallbackCurrency = storedCountry
      ? getPreferredCurrencyForCountry(storedCountry, COMMON_CURRENCY_CODES)
      : DEFAULT_CURRENCY;

    localStorage.setItem(USER_CURRENCY_STORAGE_KEY, fallbackCurrency);
    return fallbackCurrency;
  }

  return storedCurrency;
};

const getLocationCurrency = () => {
  const storedCountry = getStoredUserCountryCode() || getBrowserLocaleCountryCode();
  if (!storedCountry) {
    return DEFAULT_CURRENCY;
  }

  return getPreferredCurrencyForCountry(storedCountry, COMMON_CURRENCY_CODES);
};

export const usePreferredCurrency = () => {
  const [selectedCurrency, setSelectedCurrency] = useState(getStoredCurrency);

  useEffect(() => {
    const syncCurrency = () => {
      setSelectedCurrency(getStoredCurrency());
    };

    const syncLocationCurrency = () => {
      if (localStorage.getItem(USER_CURRENCY_STORAGE_KEY)) {
        syncCurrency();
        return;
      }

      const nextCurrency = getLocationCurrency();
      localStorage.setItem(USER_CURRENCY_STORAGE_KEY, nextCurrency);
      setSelectedCurrency(nextCurrency);
    };

    window.addEventListener('currency-initialized', syncCurrency);
    window.addEventListener(LOCATION_INITIALIZED_EVENT, syncLocationCurrency);
    window.addEventListener('storage', syncCurrency);

    return () => {
      window.removeEventListener('currency-initialized', syncCurrency);
      window.removeEventListener(LOCATION_INITIALIZED_EVENT, syncLocationCurrency);
      window.removeEventListener('storage', syncCurrency);
    };
  }, []);

  return {
    selectedCurrency,
    setSelectedCurrency,
  };
};
